import { crossLedgerLinkKeys, type CrossLedgerLink } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'

export type { CrossLedgerLink }

export interface UseCrossLedgerLinksOptions {
  readonly ledgerId: () => string | undefined
  readonly entryIds: () => readonly string[]
  readonly enabled: () => boolean
}

/** Keyed by entry id, so a badge looks itself up rather than scanning the batch. */
export type CrossLedgerLinksByEntryId = ReadonlyMap<string, CrossLedgerLink>

export function useCrossLedgerLinks(
  options: UseCrossLedgerLinksOptions,
): ComputedRef<CrossLedgerLinksByEntryId> {
  const api = useApi()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.entryIds().length > 0,
  )

  const linksQuery = useQuery({
    queryKey: computed(() => crossLedgerLinkKeys.list(options.ledgerId() ?? '', options.entryIds())),
    queryFn: ({ signal }) => api.crossLedgerLinks.list(options.ledgerId()!, options.entryIds(), signal),
    enabled: active,
    retry: false,
  })

  return computed(() => {
    const map = new Map<string, CrossLedgerLink>()
    for (const link of linksQuery.data.value ?? []) map.set(link.entryId, link)
    return map
  })
}
